import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

import { useProductReadContext } from '@context/ProductContext'

import { Operator, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { AccountGroupType } from '@model/AccountGroupType'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { PendingUnSubscriptionsRequestType, SubscriptionCartItem } from '@model/SubscriptionCart'
import { SubscriptionRequestState } from '@model/SubscriptionRequestState'
import { isSubscriptionPending } from '@model/SubscriptionTypes'
import {
  SubscriptionHistoryItem,
  SubscriptionStatus,
  SubscriptionType,
} from '@model/SubscriptionsDto'
import { SubscriptionRequestType } from '@model/UpdateSubscriptionRequest'

import { scrollToTop } from '@hooks/useScrollToTop'
import { useLocallyPersistedState } from '@hooks/useStorage'

import { useTraderRoomRoutesConditions } from '../../Traders-Room/TradersRoom.routes'

const DEFAULT_PAGE_SIZE = 50
export const CartPersistedKey = 'hasCartItems'
export const CartLinkPersistedKey = 'cartLink'

interface useCartItemsDataProps {
  isExpanded: boolean
  setIsExpanded: Dispatch<SetStateAction<boolean>>
  scrollToCart: () => void
  forceRefreshCartItems: () => void
  cartLink: string
  noneActiveSubscriptions: SubscriptionCartItem[]
  activeSubscriptions: SubscriptionHistoryItem[]
  pendingUnsubscriptions: {
    items: SubscriptionHistoryItem[]
    itemsCount: number
  }
  pendingUnsubscriptionsData: SubscriptionHistoryItem[]
  isPendingUnsubscriptionsLoading: boolean
  isActiveSubscriptionsLoading: boolean
  isNoneActiveSubscriptionsLoading: boolean
  hasSubscriptions: boolean
  upgradeOrDowngradeSubscriptionId: any
}

export const CartDataFetcher: React.FC = () => {
  const { isDefaultCFDProductType } = useProductReadContext()

  if (!isDefaultCFDProductType()) {
    return <CartDataWrapper />
  } else {
    return null
  }
}

export const CartDataWrapper: React.FC = () => {
  useCartItemsData()
  return null
}

export const useAvailableToWithdraw = (): {
  availableToWithdraw: number | undefined
  isTradingAccountsLoading: boolean
} => {
  const clientApiClient = useApiClient(ClientApiClient)

  const { data: tradingAccount, isLoading: isTradingAccountsLoading } = useQuery({
    queryKey: queryKeys.subscriptionAvailableToWithdraw(),
    queryFn: async () => {
      const [tradingAccount] = (
        await clientApiClient.getTradingAccounts({
          search: {
            AccountGroupType: {
              value: AccountGroupType.Live,
              operator: Operator.EQUAL,
            },
            Platform: {
              value: PlatformTypeEnum.CQGCAST,
              operator: Operator.EQUAL,
            },
            Status: {
              value: 1,
              operator: Operator.EQUAL,
            },
          },
        })
      ).items
      return tradingAccount?.platformOverview
    },
  })

  const availableToWithdraw = tradingAccount?.netLiquidationValueUSD

  return { availableToWithdraw, isTradingAccountsLoading }
}

export const useCartItemsData = (): useCartItemsDataProps => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [, setHasCartItems] = useLocallyPersistedState<boolean>(CartPersistedKey, false)
  const [, setCartLink] = useLocallyPersistedState<string>(CartLinkPersistedKey, '')
  const clientApiClient = useApiClient(ClientApiClient)
  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()
  const queryClient = useQueryClient()

  const cartItemsQueryKey = queryKeys.subscriptionCartItems(isTradersRoomVisible)
  const activeQueryKey = queryKeys.subscriptionActive(isTradersRoomVisible)

  const { data: noneActiveSubscriptions = [], isLoading: isNoneActiveSubscriptionsLoading } =
    useQuery({
      queryKey: cartItemsQueryKey,
      queryFn: () => {
        if (isTradersRoomVisible) {
          return clientApiClient.getSubscriptionCartItems()
        }
        return Promise.resolve([] as SubscriptionCartItem[])
      },
    })

  const { data: getActiveSubscriptions = { items: [] }, isLoading: isActiveSubscriptionsLoading } =
    useQuery({
      queryKey: activeQueryKey,
      queryFn: async () => {
        if (isTradersRoomVisible) {
          return await clientApiClient.getActiveSubscriptions({
            pageSize: DEFAULT_PAGE_SIZE,
            search: `(StateId == ${SubscriptionStatus.PENDING}) OR (StateId == ${SubscriptionStatus.DEDUCTING_FUNDS}) OR (StateId == ${SubscriptionStatus.IN_PROCESS}) OR (StateId == ${SubscriptionStatus.ACTIVE})`,
          })
        }
        return Promise.resolve({ items: [] as SubscriptionHistoryItem[] })
      },
    })

  const activeSubscriptions = useMemo(() => {
    const uniqueActiveSubscriptionId = new Set<string>()
    return getActiveSubscriptions?.items.filter((item) => {
      if (uniqueActiveSubscriptionId.has(item.subscriptionDetailId)) {
        return false
      }
      uniqueActiveSubscriptionId.add(item.subscriptionDetailId)
      return true
    })
  }, [getActiveSubscriptions])

  const getActiveSubscriptionsDetailId = useMemo(
    () => activeSubscriptions.map((item) => item?.subscriptionDetailId),
    [activeSubscriptions]
  )

  const getNonActiveSubscriptionsDetailId = useMemo(
    () => noneActiveSubscriptions.map((item) => item?.subscriptionDetail?.id),
    [noneActiveSubscriptions]
  )

  const hasSubscriptions = useMemo(() => {
    const subscriptionNoneActiveIds = noneActiveSubscriptions.map(
      (x) => x?.subscription?.subscriptionGroupType?.id
    )

    const subscriptionActiveIds = activeSubscriptions.map((x) => x?.subscriptionGroupType?.id)

    const subscriptionIds = subscriptionNoneActiveIds.concat(subscriptionActiveIds)

    const hasSubscriptionPlatform = subscriptionIds?.some((subscriptionsId) => {
      return subscriptionsId === SubscriptionType.PLATFORM
    })

    const hasSubscriptionMarketData = subscriptionIds?.some((subscriptionsId) => {
      return subscriptionsId === SubscriptionType.MARKET_DATA
    })

    return hasSubscriptionPlatform && hasSubscriptionMarketData
  }, [noneActiveSubscriptions, activeSubscriptions])

  const getSubscriptionsDetailId = [
    ...getActiveSubscriptionsDetailId,
    ...getNonActiveSubscriptionsDetailId,
  ]

  const pendingUnsubscriptionsQueryKey = queryKeys.subscriptionPendingUnsubscriptions(
    isTradersRoomVisible,
    getSubscriptionsDetailId.join(',')
  )

  const {
    data: pendingUnsubscriptions = { items: [], itemsCount: 0 },
    isLoading: isPendingUnsubscriptionsLoading,
  } = useQuery({
    queryKey: pendingUnsubscriptionsQueryKey,
    queryFn: async () => {
      if (isTradersRoomVisible) {
        return await clientApiClient.getActiveSubscriptions({
          search: {
            StateId: PendingUnSubscriptionsRequestType.StateId,
            RequestTypeId: PendingUnSubscriptionsRequestType.RequestTypeId,
          },
          pageSize: DEFAULT_PAGE_SIZE,
          SubscriptionsDetailId: getSubscriptionsDetailId.join(','),
        })
      }
      return Promise.resolve({ items: [] as SubscriptionHistoryItem[], itemsCount: 0 })
    },
  })

  const pendingUnsubscriptionsData = pendingUnsubscriptions?.items

  const scrollToCart = () => {
    scrollToTop()
    !isExpanded && setIsExpanded(true)
  }

  const forceRefreshCartItems = () => {
    queryClient.invalidateQueries({ queryKey: pendingUnsubscriptionsQueryKey })
    queryClient.invalidateQueries({ queryKey: cartItemsQueryKey })
    queryClient.invalidateQueries({ queryKey: activeQueryKey })
  }

  const cartLink = useMemo(() => {
    if (noneActiveSubscriptions?.length) {
      const hasMarketData = noneActiveSubscriptions.some(
        ({ subscription }) =>
          subscription.subscriptionGroupType?.id === SubscriptionType.MARKET_DATA
      )
      const hasTradingPlatform = noneActiveSubscriptions.some(
        ({ subscription }) => subscription.subscriptionGroupType?.id === SubscriptionType.PLATFORM
      )
      return hasTradingPlatform && !hasMarketData
        ? '/dashboard/subscriptions/trading-platforms'
        : '/dashboard/subscriptions/market-data'
    }

    if (activeSubscriptions?.length) {
      const hasMarketData = activeSubscriptions.some(
        (sub) => sub.subscriptionGroupType.id === SubscriptionType.MARKET_DATA
      )
      const hasTradingPlatform = activeSubscriptions.some(
        (sub) => sub.subscriptionGroupType.id === SubscriptionType.PLATFORM
      )
      return hasTradingPlatform && !hasMarketData
        ? '/dashboard/subscriptions/trading-platforms'
        : '/dashboard/subscriptions/market-data'
    }

    return '/dashboard/subscriptions/market-data'
  }, [activeSubscriptions, noneActiveSubscriptions])

  const isUpgradeOrDowngrade = (subscriptionHistoryItem: SubscriptionHistoryItem) =>
    subscriptionHistoryItem.state.name === SubscriptionRequestState.Pending &&
    (subscriptionHistoryItem.type.id === SubscriptionRequestType.Resubscribe ||
      subscriptionHistoryItem.type.id === SubscriptionRequestType.Update)

  const upgradeOrDowngradeSubscriptionId = useMemo(
    () =>
      activeSubscriptions
        .filter((activeSubscription) => isUpgradeOrDowngrade(activeSubscription))
        .map((activeSubscription) => activeSubscription.subscription.id),
    [activeSubscriptions]
  )

  useEffect(() => {
    setHasCartItems(
      activeSubscriptions.filter(isSubscriptionPending).length > 0 ||
        noneActiveSubscriptions.length > 0
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubscriptions, noneActiveSubscriptions])

  useEffect(() => setCartLink(cartLink), [cartLink, setCartLink])

  return {
    isExpanded,
    setIsExpanded,
    scrollToCart,
    forceRefreshCartItems,
    cartLink,
    noneActiveSubscriptions,
    activeSubscriptions,
    pendingUnsubscriptions,
    pendingUnsubscriptionsData,
    isPendingUnsubscriptionsLoading,
    isActiveSubscriptionsLoading,
    isNoneActiveSubscriptionsLoading,
    hasSubscriptions,
    upgradeOrDowngradeSubscriptionId: new Set<string>(upgradeOrDowngradeSubscriptionId),
  }
}
